import * as React from 'react';
import { myCameraStream } from 'state/video';
import { useMediaPlayback } from 'hooks/useMediaPlayback';
import { deriveAudioSinkStream, isOwnCameraStream } from 'utils/ui/videoSink';

export interface IAudioSinkProps {
  stream: MediaStream;
  /** The remote's own mic-mute signal. The sink stays mounted — only its `muted` flag follows. */
  muted?: boolean;
}

/**
 * The one place remote audio is rendered (AVS-4397): a hidden `<audio>` fed with the stream's
 * AUDIO tracks only. Mounted once per remote by `RemoteAudioSinks`, independent of any tile, so
 * every party in a call is heard for as long as it is connected — no matter how many tiles the
 * layout shows or who the active speaker is.
 *
 * Audio-only on purpose (AVS-4384): a `<video>` holding a FRAMELESS video track — the counterpart
 * replaced its camera sender with `replaceTrack(null)` — never leaves HAVE_NOTHING and takes the
 * stream's audio down with it; an `<audio>` element has no video track to stall on. The derived
 * stream is memoised on the SOURCE stream's identity: the WebRTC layer emits a new MediaStream per
 * track change (`ontrack`), while store ticks that only re-rank or re-flag a connection keep the
 * same object — so a speaker change never reassigns `srcObject` (no clipped handover).
 *
 * `hidden` keeps it out of layout — `display: none` does not stop audio playback.
 */
export const AudioSink: React.FC<IAudioSinkProps> = ({ stream, muted = false }) => {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  // Track-identity, not reference-identity: a camera re-grab swaps the stream object, and the
  // user's own microphone must never be played back at them (echo).
  const isOwnCamera = isOwnCameraStream(stream, myCameraStream().stream);
  const baseMuted = isOwnCamera || muted;
  const sinkStream = React.useMemo(() => (isOwnCamera ? null : deriveAudioSinkStream(stream)), [stream, isOwnCamera]);

  // Runs inside the gesture that lifts a forced mute (WebKit honours it only there); the rendered
  // `muted` prop below matches once `forcedMute` flips back.
  const restoreMute = React.useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = baseMuted;
    }
  }, [baseMuted]);
  const { forcedMute, play, handlePause } = useMediaPlayback(audioRef, restoreMute);

  React.useEffect(() => {
    if (audioRef.current) {
      audioRef.current.srcObject = sinkStream;
    }
    play();
  }, [sinkStream, play]);

  return (
    <audio
      ref={audioRef}
      className="hidden"
      muted={baseMuted || forcedMute}
      autoPlay
      controls={false}
      onLoadedMetadata={play}
      onPause={handlePause}
    />
  );
};
